import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { api } from '../api/client';
import type { CarouselEvent, Member } from '../types';
import { format } from 'date-fns';
import { parseEventDate } from '../utils/eventDate';
import type { EventNavState } from '../utils/eventNav';

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5"
      className={`transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function SidebarSection({
  label, icon, isOpen, onToggle, count, children,
}: {
  label: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors group"
      >
        <span className="text-slate-400 group-hover:text-indigo-400 transition-colors">{icon}</span>
        <span className="flex-1 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {count > 0 && (
          <span className="text-[10px] text-slate-400 font-medium">{count}</span>
        )}
        <ChevronIcon open={isOpen} />
      </button>
      {isOpen && (
        <div className="ml-2 mt-0.5 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

function SidebarItem({
  label, sublabel, count, active, onClick,
}: {
  label: string;
  sublabel?: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
        active
          ? 'bg-indigo-50 text-indigo-700 font-medium'
          : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="truncate leading-snug">{label}</p>
        {sublabel && (
          <p className="text-[11px] text-slate-400 truncate leading-snug">{sublabel}</p>
        )}
      </div>
      {count !== undefined && (
        <span className="text-[11px] text-slate-400 flex-shrink-0">{count}</span>
      )}
    </button>
  );
}

export function EventsSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const [events, setEvents] = useState<CarouselEvent[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  // Only once the circle has loaded can "not in it" be told apart from "not known yet".
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [open, setOpen] = useState({ when: true, where: true, who: true, what: false });
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    api.get('/events?sort=date')
      .then(({ data }) => setEvents(Array.isArray(data?.events) ? data.events : []))
      .catch(() => {});
    api.get('/account/members')
      .then(({ data }) => {
        setMembers(Array.isArray(data) ? data : []);
        setMembersLoaded(Array.isArray(data));
      })
      .catch(() => {});
  }, [location.key]);

  const memberByUserId = new Map(members.map(m => [m.userId, m]));

  // When: years
  const byYear: Record<number, number> = {};
  for (const e of events) {
    const year = e.eventDate ? parseEventDate(e.eventDate).getFullYear() : 0;
    byYear[year] = (byYear[year] || 0) + 1;
  }
  const years = Object.entries(byYear)
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([year, count]) => ({ year: Number(year), count }));

  // Where: locations
  const byLocation: Record<string, number> = {};
  for (const e of events) {
    if (e.location) byLocation[e.location] = (byLocation[e.location] || 0) + 1;
  }
  const locations = Object.entries(byLocation)
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(([location, count]) => ({ location, count }));

  // Who: participants with relationship. Someone tagged who has since left the circle
  // (their membership is gone, their tags stay) shows by name from the event list;
  // tags whose user record is gone too are grouped into one "Former members" row.
  const byPerson: Record<string, { name: string; relationship: string | null; count: number }> = {};
  const formerIds = new Set<string>();
  const formerEvents = new Set<string>();
  for (const e of events) {
    for (const p of e.participants || []) {
      const member = memberByUserId.get(p.userId);
      if (!member && !p.name) {
        formerIds.add(p.userId);
        formerEvents.add(e.id);
        continue;
      }
      if (!byPerson[p.userId]) {
        byPerson[p.userId] = {
          name: member?.user.name || p.name || 'Unknown',
          relationship: member ? member.relationship ?? null
            : membersLoaded ? 'No longer in your circle' : null,
          count: 0,
        };
      }
      byPerson[p.userId].count++;
    }
  }
  const people = Object.entries(byPerson)
    .sort(([, a], [, b]) => a.name.localeCompare(b.name))
    .map(([userId, data]) => ({ userId, ...data }));
  if (formerEvents.size > 0) {
    // the filter takes a comma-separated list of user ids (EventsList)
    people.push({ userId: [...formerIds].join(','), name: 'Former members', relationship: null, count: formerEvents.size });
  }

  // What: recent event titles
  const recentEvents = [...events]
    .sort((a, b) => {
      if (!a.eventDate && !b.eventDate) return 0;
      if (!a.eventDate) return 1;
      if (!b.eventDate) return -1;
      return new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime();
    })
    .slice(0, 25);
  const recentEventsNav: EventNavState = { eventNav: { items: recentEvents.map(e => ({ id: e.id, title: e.title })) } };

  const activeWhen = searchParams.get('when');
  const activeWhere = searchParams.get('where');
  const activeWho = searchParams.get('who');
  const hasFilter = !!(activeWhen || activeWhere || activeWho);

  function toggle(key: keyof typeof open) {
    setOpen(o => ({ ...o, [key]: !o[key] }));
  }

  function navFilter(param: string, value: string) {
    navigate(`/events?${param}=${encodeURIComponent(value)}`);
    onNavigate?.();
  }

  return (
    <div className="p-3 pt-5">
      {hasFilter && (
        <button
          onClick={() => { navigate('/events'); onNavigate?.(); }}
          className="w-full mb-3 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 text-xs font-medium hover:bg-indigo-100 transition-colors"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
          Clear filter
        </button>
      )}

      <SidebarSection
        label="When"
        icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
          </svg>
        }
        isOpen={open.when}
        onToggle={() => toggle('when')}
        count={years.length}
      >
        {years.map(({ year, count }) => (
          <SidebarItem
            key={year}
            label={year === 0 ? 'Unknown year' : String(year)}
            count={count}
            active={activeWhen === String(year)}
            onClick={() => navFilter('when', String(year))}
          />
        ))}
        {years.length === 0 && (
          <p className="px-3 py-1 text-xs text-slate-400">No events yet</p>
        )}
      </SidebarSection>

      <SidebarSection
        label="Where"
        icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
            <circle cx="12" cy="9" r="2.5" />
          </svg>
        }
        isOpen={open.where}
        onToggle={() => toggle('where')}
        count={locations.length}
      >
        {locations.map(({ location, count }) => (
          <SidebarItem
            key={location}
            label={location}
            count={count}
            active={activeWhere === location}
            onClick={() => navFilter('where', location)}
          />
        ))}
        {locations.length === 0 && (
          <p className="px-3 py-1 text-xs text-slate-400">No locations yet</p>
        )}
      </SidebarSection>

      <SidebarSection
        label="Who"
        icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        }
        isOpen={open.who}
        onToggle={() => toggle('who')}
        count={people.length}
      >
        {people.map(({ userId, name, relationship, count }) => (
          <SidebarItem
            key={userId}
            label={name}
            sublabel={relationship || undefined}
            count={count}
            active={activeWho === userId}
            onClick={() => navFilter('who', userId)}
          />
        ))}
        {people.length === 0 && (
          <p className="px-3 py-1 text-xs text-slate-400">No people tagged yet</p>
        )}
      </SidebarSection>

      <SidebarSection
        label="What"
        icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <line x1="10" y1="9" x2="8" y2="9" />
          </svg>
        }
        isOpen={open.what}
        onToggle={() => toggle('what')}
        count={recentEvents.length}
      >
        {recentEvents.map(e => (
          <SidebarItem
            key={e.id}
            label={e.title}
            sublabel={e.eventDate ? format(parseEventDate(e.eventDate), 'MMM yyyy') : undefined}
            active={false}
            onClick={() => { navigate(`/events/${e.id}`, { state: recentEventsNav }); onNavigate?.(); }}
          />
        ))}
        {recentEvents.length === 0 && (
          <p className="px-3 py-1 text-xs text-slate-400">No events yet</p>
        )}
      </SidebarSection>
    </div>
  );
}
